import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Container, Grid, Hidden, useTheme } from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import classnames from 'classnames';

import { useAppParams } from '@klp/core/app/useAppParams';
import { Block, Typography } from '@klp/design';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { Link } from '@klp/design/Link';
import { Logo } from '@klp/design/Logo';
import { useKlpStyles } from '@klp/design/Theme';
import { useNySak } from '@klp/sak/hooks/useNySak';

import { getDefaultLanguage } from '../Sidebar/config';

const footerStyles = (theme: Theme) => ({
    footer: {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.veryLight,
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(6),
        marginTop: theme.spacing(6),
        [theme.breakpoints.down('xs')]: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
        },
        display: 'flex',
        flex: '1 1 auto',
    },
    logo: {
        height: '58px',
        top: '8px',
        left: '8px',
        width: '66px',
    },
    contactRow: {
        justifyContent: 'center',
        fontSize: '25px',
        paddingTop: '10px',
    },
    privacyLinkRow: {
        justifyContent: 'center',
        paddingTop: '40px',
    },
    emailRow: {
        [theme.breakpoints.down('xs')]: {
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2),
        },
    },
    linkRow: {
        paddingTop: '10px',
    },
});

export const Footer: React.FC = () => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, footerStyles);
    const theme = useTheme();
    const location = useLocation();
    const { selectedClient } = useAppParams();
    const { goToNySak, isLoading: nySakLoading } = useNySak({
        kundeNummer: selectedClient?.kundeNummer,
    });

    const renderPhoneLink = (): JSX.Element => {
        const lang = getDefaultLanguage();
        const urlKey = `footer.phone.${lang}.url`;
        const titleKey = `footer.phone.${lang}.title`;

        return (
            <Link
                klpTypographyVariant="beta"
                href={t(urlKey)}
                translation={titleKey}
            />
        );
    };

    const renderOpeningHours = (): JSX.Element => {
        const lang = getDefaultLanguage();
        const translationKey = `footer.openingHours.${lang}`;

        return (
            <Typography
                klpTypographyVariant="small"
                klpColor="medium"
                marginVertical="tiny"
                translation={translationKey}
            />
        );
    };

    const renderPrivacyLink = (): JSX.Element => {
        const lang = getDefaultLanguage();
        const urlKey = `footer.privacy.url.${lang}`;

        return (
            <Link
                className={classes.privacyLinkRow}
                marginTop="bulky"
                href={t(urlKey)}
                target="_blank"
                translation="footer.privacy.description"
            />
        );
    };

    const renderEmailLink = (): JSX.Element => {
        const lang = getDefaultLanguage();
        const urlKey = `footer.email.url.${lang}`;

        return (
            <Link
                klpTypographyVariant="beta"
                href={t(urlKey)}
                translation="footer.email.text"
            />
        );
    };

    return (
        <footer className={classnames('Footer', classes.footer)}>
            <Container maxWidth={theme.layout.maxWidth}>
                <Grid container={true} spacing={3}>
                    <Grid item={true} xs={12} sm={6} md={4}>
                        <Grid item={true} xs={12} sm={6} md={4}>
                            <Grid
                                item={true}
                                xs={12}
                                sm={6}
                                className={classes.emailRow}
                            >
                                <Grid container={true}>
                                    <Grid
                                        item={true}
                                        className={classes.contactRow}
                                    >
                                        <Hidden xsDown={true}>
                                            <Grid item={true} xs={12} sm={6}>
                                                <Logo
                                                    className={classes.logo}
                                                />
                                            </Grid>
                                        </Hidden>
                                        <Hidden smUp={true}>
                                            <Grid item={true} xs={12} md={2}>
                                                <Typography marginTop="bulky">
                                                    <Logo
                                                        className={classes.logo}
                                                    />
                                                </Typography>
                                            </Grid>
                                        </Hidden>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item={true} xs={12} sm={6}>
                            <Grid container={true}>
                                <Grid item={true} xs={12}>
                                    <Typography translation="footer.contactIntro" />
                                    <Grid container={true}>
                                        <Grid
                                            item={true}
                                            className={classes.contactRow}
                                        >
                                            <Illustration
                                                icon={IconName.PHONE}
                                                size="medium"
                                                klpPalette="primary"
                                                klpColor="main"
                                                marginRight="tiny"
                                                justifyContent="center"
                                            />
                                        </Grid>
                                        <Grid
                                            item={true}
                                            className={classes.contactRow}
                                        >
                                            {renderPhoneLink()}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item={true} xs={12}>
                                    <Grid item={true}>
                                        <Grid
                                            item={true}
                                            className={classes.contactRow}
                                        >
                                            {renderOpeningHours()}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid container={true}>
                                    {renderPrivacyLink()}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item={true} xs={12} sm={6} md={4}>
                        <Grid
                            item={true}
                            xs={12}
                            sm={6}
                            className={classes.emailRow}
                        >
                            <Grid container={true}>
                                <Grid
                                    item={true}
                                    className={classes.contactRow}
                                >
                                    <Illustration
                                        icon={IconName.MAIL}
                                        size="medium"
                                        klpPalette="primary"
                                        klpColor="main"
                                        marginRight="tiny"
                                        justifyContent="center"
                                    />
                                </Grid>
                                <Grid
                                    item={true}
                                    className={classes.contactRow}
                                >
                                    {renderEmailLink()}
                                </Grid>
                                {selectedClient && (
                                    <Block paddingTop="tiny">
                                        <Grid item={true}>
                                            <Grid
                                                container={true}
                                                direction="column"
                                            >
                                                <Link
                                                    className={classes.linkRow}
                                                    margin-top="bulky"
                                                    href="https://www.paleet.no"
                                                    target="_blank"
                                                >
                                                    Paleet
                                                </Link>
                                                <Link
                                                    className={classes.linkRow}
                                                    href="https://www.glasmagasinet.no"
                                                    target="_blank"
                                                >
                                                    Glasmagasinet
                                                </Link>
                                                <Link
                                                    className={classes.linkRow}
                                                    href="https://www.fornebu-s.no"
                                                    target="_blank"
                                                >
                                                    Fornebu S
                                                </Link>
                                                <Link
                                                    className={classes.linkRow}
                                                    href="https://www.hvaltorvet.no"
                                                    target="_blank"
                                                >
                                                    Hvaltorvet
                                                </Link>
                                                <Link
                                                    className={classes.linkRow}
                                                    href="https://www.byporten.no"
                                                    target="_blank"
                                                >
                                                    Byporten
                                                </Link>
                                            </Grid>
                                        </Grid>
                                    </Block>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item={true} xs={12} sm={6} md={4}>
                        <Grid
                            item={true}
                            xs={12}
                            sm={6}
                            className={classes.emailRow}
                        >
                            <Grid container={true}>
                                <Grid
                                    item={true}
                                    className={classes.contactRow}
                                >
                                    <Illustration
                                        icon={IconName.CHAT}
                                        size="medium"
                                        klpPalette="primary"
                                        klpColor="main"
                                        marginRight="tiny"
                                        justifyContent="center"
                                    />
                                </Grid>
                                <Grid
                                    item={true}
                                    className={classes.contactRow}
                                >
                                    <Link
                                        klpTypographyVariant="beta"
                                        onClick={() =>
                                            goToNySak({
                                                pathname: location.pathname,
                                            })
                                        }
                                        translation="footer.newSak.text"
                                        disabled={nySakLoading}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Container>
        </footer>
    );
};

Footer.displayName = 'Footer';
